/** The one place a chart reads a colour, a type size or a transition from (ADR-0090).
 *
 * **Every colour here is a CSS custom property reference, never a literal.** ADR-0007 makes a
 * colour literal outside `tokens.css` a stylelint failure for CSS; a chart is written in TSX, which
 * stylelint cannot see, so the rule has to be kept by construction: a mark's `fill` is one of the
 * strings below or it is not a colour this product draws. A literal here would also be a colour
 * that does not follow a token change, which is how the fifth chart comes to disagree with the
 * first.
 *
 * **The brand green is not in this list and must not be added.** `--accent` is chrome. A chart that
 * painted a bar green would be saying that number is good news, and an emissions total is not.
 * Scope marks are `--s1` and `--s2` (told apart by hue, not by goodness), a state that needs
 * somebody is `--warn`, and everything structural is a neutral.
 */
export const CHART = {
  scope1: "var(--s1)",
  scope2: "var(--s2)",
  /** "Passed", and a share of something done. Never an emissions figure (see above). */
  ok: "var(--ok)",
  /** Not assessed / not available: a neutral, never a short bar. */
  unassessed: "var(--muted)",
  /** A complete chain is information, not praise: `--info`, not `--ok` and not the accent. */
  complete: "var(--info)",
  attention: "var(--warn)",
  ink: "var(--ink)",
  inkSoft: "var(--ink-2)",
  muted: "var(--muted)",
  axis: "var(--line-2)",
  grid: "var(--line)",
  track: "var(--panel-3)",
  surface: "var(--panel)",
} as const;

/** Axis tick text. A token size, so the 12px floor (`--t-min`) holds in a chart as it does in CSS. */
export const TICK_LABEL = {
  fill: CHART.muted,
  fontSize: "var(--t-min)",
  fontFamily: "var(--font)",
} as const;

/** The type size of a label drawn inside a chart. */
export const MARK_LABEL = {
  fill: CHART.ink,
  fontSize: "var(--t-min)",
  fontFamily: "var(--font)",
} as const;

/** The colour names a chart takes as plain data, so a prop stays serialisable and a literal cannot
 * be passed. Each resolves to a token reference above. */
export type ChartColour =
  | "scope1"
  | "scope2"
  | "scope3"
  | "scope1b"
  | "scope1c"
  | "scope1d"
  | "scope2b"
  | "scope2c"
  | "scope2d"
  | "scope3b"
  | "scope3c"
  | "scope3d"
  | "info"
  | "warn"
  | "ok"
  | "unassessed"
  | "ramp1"
  | "ramp2"
  | "ramp3"
  | "ramp4"
  | "crit";

/** The dashboard band's palette (ADR-0212, P7): a teal ramp for parts of a count (documents by type,
 * stages of a pipeline, coverage), the one amber (`warn`) for "needs somebody", and the muted red
 * (`crit`) for a problem. Built with `color-mix` from tokens, so no literal exists here and dark mode
 * follows the tokens. The ramp is for counts and shares; an emissions scope uses `scope1`, `scope2`
 * or `scope3` (an estimate), and the same series is the same colour on every page because every
 * chart resolves it here. Every ramp step is at least 3:1 on white (measured: 10.1, 6.7, 4.2, 3.1)
 * and the steps differ in lightness, so a colour-blind reader tells adjacent series apart. Amber is
 * for warnings only. */
const RAMP = {
  ramp1: "color-mix(in srgb, var(--focus) 45%, var(--ink))",
  ramp2: "color-mix(in srgb, var(--focus) 70%, var(--ink))",
  ramp3: "var(--focus)",
  ramp4: "color-mix(in srgb, var(--focus) 80%, var(--panel))",
} as const;

/** **Tonnes take their scope's colour, and parts of one scope take tints of it** (design uplift U5).
 * Teal (`RAMP`) is for counts and shares of a count, never for an amount of CO2e, so "Scope 1 by
 * source" is four steps of `--s1`, not four steps of teal. A tint is the scope colour mixed into the
 * panel, so it follows the tokens in dark mode and is told apart from its neighbours by lightness. */
function tint(token: string, percent: number): string {
  return `color-mix(in srgb, ${token} ${String(percent)}%, var(--panel))`;
}

const TINTS = {
  scope1b: tint("var(--s1)", 70),
  scope1c: tint("var(--s1)", 48),
  scope1d: tint("var(--s1)", 34),
  scope2b: tint("var(--s2)", 70),
  scope2c: tint("var(--s2)", 48),
  scope2d: tint("var(--s2)", 34),
  scope3b: tint("var(--s3)", 70),
  scope3c: tint("var(--s3)", 48),
  scope3d: tint("var(--s3)", 34),
} as const;

/** The four colours of a scope, strongest first: the scope's own, then its three tints. */
export function scopeShades(
  scope: "1" | "2" | "3",
): readonly [ChartColour, ChartColour, ChartColour, ChartColour] {
  return [`scope${scope}`, `scope${scope}b`, `scope${scope}c`, `scope${scope}d`] as const;
}

export function colourOf(name: ChartColour): string {
  const map: Record<ChartColour, string> = {
    scope1: CHART.scope1,
    scope2: CHART.scope2,
    scope3: "var(--s3)",
    info: CHART.complete,
    warn: CHART.attention,
    ok: CHART.ok,
    unassessed: CHART.unassessed,
    ...RAMP,
    ...TINTS,
    crit: "var(--crit)",
  };
  return map[name];
}
